import { Extension, Range } from "@tiptap/core";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Node as PMNode } from "@tiptap/pm/model";
import type { RedactionType } from "../../db/schema";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    redactions: {
      setRedactions: (redactions: RedactionType[]) => ReturnType;
      focusRedaction: (id: string | null) => ReturnType;
      setRedactionsEnabled: (enabled: boolean) => ReturnType;
    };
  }
}

interface TextNodesWithPosition {
  text: string;
  pos: number;
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// One occurrence of a redaction in the document, in absolute doc positions.
export interface RedactionOccurrence {
  redaction: RedactionType;
  from: number;
  to: number;
}

// Finds every case-insensitive occurrence of each redaction's `text` in the
// document and returns their absolute positions. Shared by the decoration
// builder (for highlighting) and the bubble menu (to detect whether a
// selection overlaps an existing redaction). Works regardless of whether
// highlighting is currently enabled.
export function findRedactionOccurrences(
  doc: PMNode,
  redactions: RedactionType[]
): RedactionOccurrence[] {
  if (!redactions.length) return [];

  const textNodes: TextNodesWithPosition[] = [];
  let blockIndex = 0;
  doc.descendants((node, pos) => {
    if (node.isText) {
      if (textNodes[blockIndex]) {
        textNodes[blockIndex] = {
          text: textNodes[blockIndex].text + node.text,
          pos: textNodes[blockIndex].pos,
        };
      } else {
        textNodes[blockIndex] = { text: `${node.text}`, pos };
      }
    } else {
      blockIndex += 1;
    }
  });

  const occurrences: RedactionOccurrence[] = [];
  const blocks = textNodes.filter(Boolean);
  for (const block of blocks) {
    for (const redaction of redactions) {
      const needle = redaction.text?.trim();
      if (!needle) continue;
      const regex = new RegExp(escapeRegExp(needle), "giu");
      const matches = Array.from(block.text.matchAll(regex));
      for (const match of matches) {
        if (match.index === undefined || match[0] === "") continue;
        occurrences.push({
          redaction,
          from: block.pos + match.index,
          to: block.pos + match.index + match[0].length,
        });
      }
    }
  }
  return occurrences;
}

// Returns the distinct redactions whose occurrences overlap the [from, to]
// selection range. Used by the bubble menu to flip "Redact" → "Unredact".
export function getRedactionsInRange(
  doc: PMNode,
  redactions: RedactionType[],
  from: number,
  to: number
): RedactionType[] {
  const seen = new Set<string>();
  const result: RedactionType[] = [];
  for (const occ of findRedactionOccurrences(doc, redactions)) {
    // Overlap test: ranges touch if one starts before the other ends.
    if (from < occ.to && to > occ.from && !seen.has(occ.redaction.id)) {
      seen.add(occ.redaction.id);
      result.push(occ.redaction);
    }
  }
  return result;
}

// Walks the document collecting all text-node runs (similar to SearchAndReplace),
// then finds every case-insensitive occurrence of each redaction's `text` and
// emits one inline decoration per match. The focused redaction gets a second
// CSS class so the sidebar selection visually pops.
function buildDecorations(
  doc: PMNode,
  redactions: RedactionType[],
  focusedId: string | null
): { decorations: DecorationSet; matchCount: number } {
  if (!redactions.length) {
    return { decorations: DecorationSet.empty, matchCount: 0 };
  }

  const textNodes: TextNodesWithPosition[] = [];
  let blockIndex = 0;
  doc.descendants((node, pos) => {
    if (node.isText) {
      if (textNodes[blockIndex]) {
        textNodes[blockIndex] = {
          text: textNodes[blockIndex].text + node.text,
          pos: textNodes[blockIndex].pos,
        };
      } else {
        textNodes[blockIndex] = { text: `${node.text}`, pos };
      }
    } else {
      blockIndex += 1;
    }
  });

  const allDecorations: Decoration[] = [];
  let matchCount = 0;

  const blocks = textNodes.filter(Boolean);

  // A single highlighted run within a block. Adjacent redactions (e.g. a first
  // name and last name separated only by a space) get collapsed into one of
  // these so they render as a single continuous mark instead of two boxes.
  interface MergedRange {
    start: number; // block-relative start index
    end: number; // block-relative end index
    ids: Set<string>;
    focused: boolean;
  }

  for (const block of blocks) {
    // Collect every match in this block across all redactions, in block-text
    // coordinates so we can reason about the gaps between them.
    const ranges: MergedRange[] = [];
    for (const redaction of redactions) {
      const needle = redaction.text?.trim();
      if (!needle) continue;
      const regex = new RegExp(escapeRegExp(needle), "giu");
      const matches = Array.from(block.text.matchAll(regex));
      for (const match of matches) {
        if (match.index === undefined || match[0] === "") continue;
        ranges.push({
          start: match.index,
          end: match.index + match[0].length,
          ids: new Set([redaction.id]),
          focused: focusedId === redaction.id,
        });
        matchCount += 1;
      }
    }

    // Merge ranges that overlap or are separated only by whitespace. The
    // merged range spans the gap too, so the connecting space is highlighted
    // and the two redactions read as one block.
    ranges.sort((a, b) => a.start - b.start || a.end - b.end);
    const merged: MergedRange[] = [];
    for (const r of ranges) {
      const last = merged[merged.length - 1];
      const gap = last ? block.text.slice(last.end, r.start) : null;
      if (last && (r.start <= last.end || /^\s*$/.test(gap as string))) {
        last.end = Math.max(last.end, r.end);
        r.ids.forEach((id) => last.ids.add(id));
        last.focused = last.focused || r.focused;
      } else {
        merged.push({ start: r.start, end: r.end, ids: new Set(r.ids), focused: r.focused });
      }
    }

    for (const m of merged) {
      allDecorations.push(
        Decoration.inline(block.pos + m.start, block.pos + m.end, {
          class: m.focused
            ? "redaction-mark redaction-mark-focused"
            : "redaction-mark",
          // Space-separated so a merged mark can carry every redaction id it
          // covers; the sidebar focus query uses an [attr~=id] selector.
          "data-redaction-id": Array.from(m.ids).join(" "),
        })
      );
    }
  }

  return {
    decorations: DecorationSet.create(doc, allDecorations),
    matchCount,
  };
}

export const redactionsPluginKey = new PluginKey("redactionsPlugin");

export interface RedactionsStorage {
  redactions: RedactionType[];
  focusedId: string | null;
  // Master switch for editor decorations. Defaults to false so the editor
  // looks identical to a normal report until the user opts in via the
  // Redactions sidebar toggle. When false the plugin returns an empty
  // decoration set regardless of how many redactions are loaded.
  enabled: boolean;
  matchCount: number;
  // Internal tick used to force the plugin's apply() to re-run after a
  // setRedactions / focusRedaction command. ProseMirror only re-applies plugin
  // state when the doc changes or a transaction carries meta — bumping this
  // and dispatching a no-op transaction with our meta key triggers it.
  _version: number;
}

export const Redactions = Extension.create<{}, RedactionsStorage>({
  name: "redactions",

  addStorage() {
    return {
      redactions: [],
      focusedId: null,
      enabled: false,
      matchCount: 0,
      _version: 0,
    };
  },

  addCommands() {
    return {
      setRedactions:
        (redactions: RedactionType[]) =>
        ({ editor, tr, dispatch }) => {
          editor.storage.redactions.redactions = redactions || [];
          editor.storage.redactions._version += 1;
          if (dispatch) {
            dispatch(tr.setMeta(redactionsPluginKey, { redactionsUpdated: true }));
          }
          return true;
        },
      focusRedaction:
        (id: string | null) =>
        ({ editor, tr, dispatch }) => {
          editor.storage.redactions.focusedId = id;
          editor.storage.redactions._version += 1;
          if (dispatch) {
            dispatch(tr.setMeta(redactionsPluginKey, { focusUpdated: true }));
          }
          return true;
        },
      setRedactionsEnabled:
        (enabled: boolean) =>
        ({ editor, tr, dispatch }) => {
          editor.storage.redactions.enabled = enabled;
          editor.storage.redactions._version += 1;
          if (dispatch) {
            dispatch(tr.setMeta(redactionsPluginKey, { enabledUpdated: true }));
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin({
        key: redactionsPluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, oldDecorations) {
            const meta = tr.getMeta(redactionsPluginKey);
            if (!tr.docChanged && !meta) {
              return oldDecorations.map(tr.mapping, tr.doc);
            }
            const { redactions, focusedId, enabled } =
              editor.storage.redactions;
            if (!enabled) {
              editor.storage.redactions.matchCount = 0;
              return DecorationSet.empty;
            }
            const { decorations, matchCount } = buildDecorations(
              tr.doc,
              redactions,
              focusedId
            );
            editor.storage.redactions.matchCount = matchCount;
            return decorations;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

export default Redactions;
